import { useState } from 'react';
import { useAuthStore } from '../hooks/useAuth';
import { useLocation, Link } from 'wouter';
import { api } from '../lib/api';

export function CarrierLogin() {
    const { login } = useAuthStore();
    const [, setLocation] = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const data = await api.carrierLogin(email, password);
            login(data.user, data.token);

            const params = new URLSearchParams(window.location.search);
            const returnTo = params.get('from');
            setLocation(returnTo ? decodeURIComponent(returnTo) : '/carrier-dashboard');
        } catch (err: any) {
            if (err.status === 401 || err.code === 'INVALID_CREDENTIALS' || err.code === 'USER_NOT_FOUND' || err.code === 'INVALID_PASSWORD') {
                setError('Invalid email or password.');
            } else {
                setError(err.message || 'Login failed');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
                <div className="text-center mb-8">
                    <Link href="/" className="text-sm text-slate-500 hover:text-primary mb-6 inline-block transition-colors">← Back to Home</Link>
                    <div className="text-5xl mb-6 block">🚚</div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">Carrier Login</h1>
                    <p className="text-slate-500">Sign in to manage your appointments</p>
                </div>

                {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 text-sm text-center font-medium">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-700">Email Address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="dispatch@yourcompany.com"
                            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            required
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-700">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold transition disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading ? 'Authenticating...' : 'Sign In'}
                    </button>
                </form>

                <div className="mt-8 text-center text-sm text-slate-500">
                    <p><Link href="/carrier/forgot-password" className="text-primary hover:text-primary/80 transition-colors font-semibold">Forgot password?</Link></p>
                    <p className="mt-2">Don't have an account? <Link href="/carrier-signup" className="text-primary hover:text-primary/80 transition-colors font-semibold">Sign up</Link></p>
                </div>
            </div>
        </div>
    );
}
